/**
 * Multi-Asset Availability Helper
 * Manages availability tracking for multiple selected assets
 */

// Dictionary to store available times for each asset
let assetAvailabilityMap = new Map();

// Dictionary to count how many assets have each time available
let timeFrequencyMap = new Map();

/**
 * Add an asset and its available times to the tracking system
 * @param {string|number} assetId - The asset identifier
 * @param {Array<string>} availableTimes - Array of available time strings
 */
export const addAssetAvailability = (assetId, availableTimes) => {
  // Store the asset's available times
  assetAvailabilityMap.set(assetId, availableTimes);
  
  // Update time frequency counts
  availableTimes.forEach(time => {
    const currentCount = timeFrequencyMap.get(time) || 0;
    timeFrequencyMap.set(time, currentCount + 1);
  });
};

/**
 * Remove an asset and its available times from the tracking system
 * @param {string|number} assetId - The asset identifier
 */
export const removeAssetAvailability = (assetId) => {
  const availableTimes = assetAvailabilityMap.get(assetId);
  if (availableTimes) {
    // Decrement time frequency counts
    availableTimes.forEach(time => {
      const currentCount = timeFrequencyMap.get(time) || 0;
      if (currentCount > 1) {
        timeFrequencyMap.set(time, currentCount - 1);
      } else {
        timeFrequencyMap.delete(time);
      }
    });
    
    // Remove asset from availability map
    assetAvailabilityMap.delete(assetId);
  }
};

/**
 * Get the common available times across all selected assets
 * @returns {Array<string>} Array of times available for all selected assets
 */
export const getCommonAvailableTimes = () => {
  const totalAssets = assetAvailabilityMap.size;
  if (totalAssets === 0) return [];
  
  const commonTimes = [];
  timeFrequencyMap.forEach((count, time) => {
    if (count === totalAssets) {
      commonTimes.push(time);
    }
  });
  
  // Sort times chronologically instead of alphabetically
  return commonTimes.sort((a, b) => {
    // Convert time strings to Date objects for proper time comparison
    const timeA = new Date(`2000-01-01 ${a}`);
    const timeB = new Date(`2000-01-01 ${b}`);
    return timeA - timeB;
  });
};

/**
 * Clear all asset availability data
 */
export const clearAllAssetAvailability = () => {
  assetAvailabilityMap.clear();
  timeFrequencyMap.clear();
};
